import type { CampingSpot, Coordinates } from '../types';
import { getDistance } from './geometryUtils';
import { isCheckInEligible, type CheckInEligibilityOpts } from './spotProximity';
import type { GeofenceRegion } from '../src/plugins/geofence';

// Ren logikk for bakgrunns-geofence-innsjekk. Ingen Capacitor-import her (kun
// `import type`), slik at modulen er fullt testbar. Effektene (plugin-kall,
// tillatelser, i18n) ligger i hooks/useGeofenceCheckIn.ts.

/**
 * Velg de `max` nærmeste innsjekk-egnede plassene til `position`. iOS har et
 * hard-tak på 20 samtidige regioner, så vi overvåker alltid bare de nærmeste.
 */
export const selectNearestEligibleSpots = (
  spots: CampingSpot[],
  position: Coordinates,
  max: number,
  eligibility: CheckInEligibilityOpts = {},
): CampingSpot[] =>
  spots
    .filter(s => isCheckInEligible(s, eligibility))
    .map(s => ({
      s,
      d: getDistance(position.latitude, position.longitude, s.location!.latitude, s.location!.longitude),
    }))
    .sort((a, b) => a.d - b.d)
    .slice(0, Math.max(0, max))
    .map(x => x.s);

/**
 * Bygg geofence-soner fra plasser. `buildText` injiseres for i18n slik at native
 * kan poste ferdig oversatt varsel uten å kjenne oversettelser.
 */
export const buildGeofenceRegions = (
  spots: CampingSpot[],
  opts: {
    radius: number;
    dwellMs: number;
    buildText: (spot: CampingSpot) => { title: string; body: string };
  },
): GeofenceRegion[] =>
  spots
    .filter(s => s.location)
    .map(s => {
      const { title, body } = opts.buildText(s);
      return {
        spotId: s.id,
        latitude: s.location!.latitude,
        longitude: s.location!.longitude,
        radius: opts.radius,
        dwellMs: opts.dwellMs,
        title,
        body,
      };
    });

/** Hvor langt brukeren må ha flyttet seg før vi re-registrerer sonene. */
export const RESYNC_MOVE_THRESHOLD_M = 5000; // 5 km

/**
 * Bør vi re-registrere geofences? Ja hvis vi mangler et anker (aldri registrert),
 * brukeren har flyttet seg langt fra forrige anker (nærmer seg utkanten av dekket
 * område), eller settet av egnede plass-IDer har endret seg.
 */
export const shouldResyncGeofences = (
  lastAnchor: Coordinates | null,
  position: Coordinates,
  prevSpotIds: string[],
  nextSpotIds: string[],
  moveThresholdM: number = RESYNC_MOVE_THRESHOLD_M,
): boolean => {
  if (!lastAnchor) return true;
  const moved = getDistance(lastAnchor.latitude, lastAnchor.longitude, position.latitude, position.longitude);
  if (moved >= moveThresholdM) return true;
  if (prevSpotIds.length !== nextSpotIds.length) return true;
  const prevSet = new Set(prevSpotIds);
  return nextSpotIds.some(id => !prevSet.has(id));
};
